import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { Palette, Phone, Plug, MessageSquare, Inbox, Zap, ArrowRight, Calendar, CheckCircle, User, Clock, Mail, Instagram, Check, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import Image from "@/components/ui/image";

const tabs = [
  {
    id: "voice",
    icon: Palette,
    title: "White-Label",
    shortDesc: "Your brand, your platform",
  },
  {
    id: "chat",
    icon: Plug,
    title: "Integrations",
    shortDesc: "The hard part solved",
  },
  {
    id: "inbox",
    icon: Inbox,
    title: "Inbox",
    shortDesc: "All channels unified",
  },
  {
    id: "workflows",
    icon: Zap,
    title: "Workflows",
    shortDesc: "Automate everything",
  },
];

const tabContent = {
  voice: {
    title: "White-Label Platform.\nYour brand, your software.",
    description:
      "Your clients don't see Ayra.\nThey see your software.\n\nDeliver AI under your brand — custom domain, logo, and colors.\nEverything feels built in-house.",
    buttonText: "See White Label",
  },
  chat: {
    title: "Integrations kill agencies.\nNot anymore.",
    description:
      "Your client's AI is useless if it doesn't work inside their business.\nCRMs. Calendars. Software they already use.\n\nWe did the heavy lifting. One click to connect.\nNew integrations added every week.",
    buttonText: "See Integrations",
  },
  inbox: {
    title: "Unified Inbox.\nAll channels, one view.",
    description: "Every conversation in one place. Calls, texts, emails, DMs—all flowing into a single view.\nYour clients see simplicity. You see everything.\n\nReal-time updates across all channels.\nNever miss a conversation.",
    buttonText: "See Inbox",
  },
  workflows: {
    title: "Workflow Automation.\nNo Zapier needed.",
    description: "Confirmations. Reminders. Follow-ups. Review requests.\nPre-built templates ready to deploy, or drag-and-drop your own.\n\nNo Zapier. No per-task fees.\nAutomate everything in one place.",
    buttonText: "See Workflows",
  },
};

// White-Label Visual - Single card, same style as Tickets/Announcements
const WhiteLabelVisual = () => {
  const BRAND_NAME = "Acme AI Portal";
  const DOMAIN = "app.acme.com";
  
  const [animationPhase, setAnimationPhase] = useState<
    'idle' | 'typing-name' | 'typing-domain' | 'connecting' | 'connected' | 'applying' | 'complete'
  >('idle');
  const [brandName, setBrandName] = useState("");
  const [domain, setDomain] = useState("");
  const [nameFocused, setNameFocused] = useState(false);
  const [domainFocused, setDomainFocused] = useState(false);

  useEffect(() => {
    const runAnimation = () => {
      // Reset
      setBrandName("");
      setDomain("");
      setAnimationPhase('idle');
      setNameFocused(false);
      setDomainFocused(false);

      // Phase 1: Focus name and start typing (delay 600ms)
      setTimeout(() => {
        setNameFocused(true);
        setAnimationPhase('typing-name');
      }, 600);

      // Type brand name
      let nameIndex = 0;
      const nameInterval = setInterval(() => {
        if (nameIndex < BRAND_NAME.length) {
          setBrandName(BRAND_NAME.slice(0, nameIndex + 1));
          nameIndex++;
        } else {
          clearInterval(nameInterval);
        }
      }, 55);

      // Phase 2: Focus domain and start typing (after name done ~1.5s)
      setTimeout(() => {
        setNameFocused(false);
        setDomainFocused(true);
        setAnimationPhase('typing-domain');
      }, 1600);

      // Type domain
      setTimeout(() => {
        let domainIndex = 0;
        const domainInterval = setInterval(() => {
          if (domainIndex < DOMAIN.length) {
            setDomain(DOMAIN.slice(0, domainIndex + 1));
            domainIndex++;
          } else {
            clearInterval(domainInterval);
          }
        }, 70);
      }, 1600);

      // Phase 3: Connecting (after domain done ~2.8s)
      setTimeout(() => {
        setDomainFocused(false);
        setAnimationPhase('connecting');
      }, 2800);

      // Phase 4: Connected
      setTimeout(() => {
        setAnimationPhase('connected');
      }, 3600);

      // Phase 5: Applying branding
      setTimeout(() => {
        setAnimationPhase('applying');
      }, 4200);

      // Phase 6: Complete
      setTimeout(() => {
        setAnimationPhase('complete');
      }, 4800);

      // Reset for loop
      setTimeout(() => {
        setAnimationPhase('idle');
      }, 7000);
    };

    runAnimation();
    const interval = setInterval(runAnimation, 7500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="bg-card rounded-xl md:rounded-2xl p-4 md:p-6 border border-border shadow-xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 md:mb-5">
        <h3 className="font-semibold text-sm md:text-base text-foreground">White-Label Setup</h3>
        <Palette className="h-4 w-4 text-primary" />
      </div>

      {/* Brand Identity Section */}
      <div className="space-y-3 md:space-y-4 mb-4 md:mb-5">
        {/* Logo + Brand Name Row */}
        <div className="flex items-center gap-3">
          {/* Logo placeholder that transforms */}
          <motion.div
            animate={{
              backgroundColor: animationPhase === 'complete' || animationPhase === 'applying' 
                ? '#5F8F84' 
                : 'hsl(var(--secondary))',
              scale: animationPhase === 'applying' ? [1, 1.05, 1] : 1,
            }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center flex-shrink-0"
          >
            <AnimatePresence mode="wait">
              {animationPhase === 'complete' || animationPhase === 'applying' ? (
                <motion.span
                  key="branded"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  className="text-white text-sm md:text-base font-bold"
                >
                  A
                </motion.span>
              ) : (
                <motion.div
                  key="placeholder"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.4 }}
                  exit={{ opacity: 0 }}
                  className="w-5 h-5 md:w-6 md:h-6 rounded border-2 border-dashed border-muted-foreground/40"
                />
              )}
            </AnimatePresence>
          </motion.div>

          {/* Brand Name Input */}
          <div className="flex-1">
            <div className="text-[10px] md:text-xs text-muted-foreground mb-1">Brand Name</div>
            <div 
              className={`h-9 md:h-10 bg-secondary rounded-lg border px-2 md:px-3 flex items-center text-xs md:text-sm text-foreground transition-all duration-200 ${
                nameFocused ? 'border-primary ring-2 ring-primary/20' : 'border-border'
              }`}
            >
              {brandName || <span className="text-muted-foreground/50">Your brand name</span>}
              {nameFocused && (
                <motion.span
                  animate={{ opacity: [1, 0] }}
                  transition={{ duration: 0.5, repeat: Infinity, repeatType: "reverse" }}
                  className="inline-block w-0.5 h-3 md:h-4 bg-foreground ml-0.5"
                />
              )}
            </div>
          </div>
        </div>

        {/* Domain Input */}
        <div>
          <div className="text-[10px] md:text-xs text-muted-foreground mb-1">Custom Domain</div>
          <div 
            className={`h-9 md:h-10 bg-secondary rounded-lg border px-2 md:px-3 flex items-center text-xs md:text-sm transition-all duration-200 ${
              domainFocused ? 'border-primary ring-2 ring-primary/20' : 'border-border'
            }`}
          >
            <span className="text-muted-foreground/50 mr-1">https://</span>
            <span className="text-foreground">{domain}</span>
            {domainFocused && (
              <motion.span
                animate={{ opacity: [1, 0] }}
                transition={{ duration: 0.5, repeat: Infinity, repeatType: "reverse" }}
                className="inline-block w-0.5 h-3 md:h-4 bg-foreground ml-0.5"
              />
            )}
          </div>
        </div>
      </div>

      {/* Status Section */}
      <div className="bg-secondary/50 rounded-lg md:rounded-xl p-3 md:p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {/* Status indicator */}
            <motion.div
              animate={{
                backgroundColor: 
                  animationPhase === 'complete' ? '#5F8F84' :
                  animationPhase === 'applying' || animationPhase === 'connected' ? '#5F8F84' :
                  animationPhase === 'connecting' ? '#F59E0B' :
                  'hsl(var(--muted-foreground) / 0.3)',
                scale: animationPhase === 'connecting' ? [1, 1.2, 1] : 1,
              }}
              transition={{ 
                duration: animationPhase === 'connecting' ? 0.6 : 0.3,
                repeat: animationPhase === 'connecting' ? Infinity : 0,
              }}
              className="w-2 h-2 rounded-full"
            />
            
            {/* Status text */}
            <AnimatePresence mode="wait">
              <motion.span
                key={animationPhase}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.2 }}
                className={`text-xs md:text-sm font-medium ${
                  animationPhase === 'complete' || animationPhase === 'applying' || animationPhase === 'connected'
                    ? 'text-primary' 
                    : animationPhase === 'connecting'
                    ? 'text-amber-600'
                    : 'text-muted-foreground'
                }`}
              >
                {animationPhase === 'complete' ? 'Live' :
                 animationPhase === 'applying' ? 'Applying branding...' :
                 animationPhase === 'connected' ? 'Connected' :
                 animationPhase === 'connecting' ? 'Connecting...' :
                 'Waiting for setup'}
              </motion.span>
            </AnimatePresence>
          </div>

          {/* Checkmark on complete */}
          <AnimatePresence>
            {animationPhase === 'complete' && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.2 }}
                className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-primary/10"
              >
                <Check className="h-3 w-3 text-primary" />
                <span className="text-[10px] md:text-xs font-medium text-primary">Ready for clients</span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};
// Integration logos
import ghlLogo from "@/assets/ghl-logo.png";
import googleCalendarLogo from "@/assets/google-calendar-logo.png";
import hubspotLogo from "@/assets/hubspot-logo.png";
import jobberLogo from "@/assets/jobber-logo.png";

// Integrations Visual - Single card, same style as Tickets/Announcements/White Label
const IntegrationsVisual = () => {
  const integrations = [
    { id: 'gcal', name: "Google Calendar", logo: googleCalendarLogo },
    { id: 'hubspot', name: "HubSpot", logo: hubspotLogo },
    { id: 'ghl', name: "GoHighLevel", logo: ghlLogo },
    { id: 'jobber', name: "Jobber", logo: jobberLogo },
  ];
  
  const [animationPhase, setAnimationPhase] = useState<
    'idle' | 'hovering' | 'connecting' | 'connected' | 'complete'
  >('idle');

  useEffect(() => {
    const runAnimation = () => {
      setAnimationPhase('idle');

      // Phase 1: Hover Google Calendar (slower)
      setTimeout(() => {
        setAnimationPhase('hovering');
      }, 1200);

      // Phase 2: Connecting (slower)
      setTimeout(() => {
        setAnimationPhase('connecting');
      }, 2400);

      // Phase 3: Connected (slower)
      setTimeout(() => {
        setAnimationPhase('connected');
      }, 4000);

      // Phase 4: Complete state
      setTimeout(() => {
        setAnimationPhase('complete');
      }, 5200);

      // Reset for loop (longer hold)
      setTimeout(() => {
        setAnimationPhase('idle');
      }, 8500);
    };

    runAnimation();
    const interval = setInterval(runAnimation, 9000);
    return () => clearInterval(interval);
  }, []);

  // Only Google Calendar (index 0) animates, others stay static at "Available"
  const getIntegrationStatus = (index: number) => {
    if (index !== 0) return 'available';
    
    if (animationPhase === 'idle') return 'available';
    if (animationPhase === 'hovering') return 'hover';
    if (animationPhase === 'connecting') return 'connecting';
    if (animationPhase === 'connected' || animationPhase === 'complete') return 'connected';
    return 'available';
  };

  return (
    <div className="scale-[0.79] origin-top">
      <div className="bg-card/80 backdrop-blur-sm rounded-xl md:rounded-2xl p-4 md:p-6 border border-emerald-500/10 dark:border-emerald-400/20 shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 md:mb-5">
          <h3 className="font-semibold text-sm md:text-base text-foreground">Connect Integrations</h3>
          <Plug className="h-4 w-4 text-primary" />
        </div>

        {/* Integrations List */}
        <div className="space-y-2 md:space-y-2.5 mb-4 md:mb-5">
          {integrations.map((integration, i) => {
            const status = getIntegrationStatus(i);
            
            return (
              <motion.div
                key={integration.id}
                animate={{
                  backgroundColor: status === 'hover' ? 'hsl(var(--secondary))' : 'transparent',
                  scale: status === 'hover' ? 1.01 : 1,
                }}
                transition={{ duration: 0.25 }}
                className={`flex items-center gap-3 p-2.5 md:p-3 rounded-lg border transition-colors ${
                  status === 'connected' 
                    ? 'bg-primary/5 border-primary/20' 
                    : status === 'hover' || status === 'connecting'
                    ? 'border-primary/30'
                    : 'border-border bg-secondary/30'
                }`}
              >
                {/* Logo */}
                <div className="w-8 h-8 md:w-9 md:h-9 rounded-lg bg-white border border-border/50 flex items-center justify-center overflow-hidden p-1.5 flex-shrink-0">
                  <Image 
                    src={integration.logo} 
                    alt={integration.name}
                    width={32}
                    height={32}
                    className="w-full h-full object-contain"
                  />
                </div>
                
                {/* Name */}
                <div className="flex-1 min-w-0">
                  <div className="text-xs md:text-sm font-medium text-foreground">{integration.name}</div>
                </div>
                
                {/* Status / Action - Only Google Calendar animates */}
                <AnimatePresence mode="wait">
                  {status === 'connected' ? (
                    <motion.div
                      key="connected"
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      transition={{ duration: 0.35 }}
                      className="flex items-center gap-1.5"
                    >
                      <div className="w-4 h-4 md:w-5 md:h-5 rounded-full bg-primary flex items-center justify-center">
                        <Check className="w-2.5 h-2.5 md:w-3 md:h-3 text-white" />
                      </div>
                      <span className="text-[10px] md:text-xs font-medium text-primary">Connected</span>
                    </motion.div>
                  ) : status === 'connecting' ? (
                    <motion.div
                      key="connecting"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.35 }}
                      className="flex items-center gap-1.5"
                    >
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1.4, repeat: Infinity, ease: "linear" }}
                        className="w-4 h-4 md:w-5 md:h-5 rounded-full border-2 border-primary border-t-transparent"
                      />
                      <span className="text-[10px] md:text-xs font-medium text-primary">Connecting...</span>
                    </motion.div>
                  ) : status === 'hover' ? (
                    <motion.div
                      key="connect-btn"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.25 }}
                      className="px-2.5 md:px-3 py-1 md:py-1.5 rounded-md bg-primary text-primary-foreground text-[10px] md:text-xs font-medium"
                    >
                      Connect
                    </motion.div>
                  ) : (
                    <span className="text-[10px] md:text-xs text-muted-foreground">
                      Available
                    </span>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

        {/* Footer Status */}
        <div className="bg-secondary/50 rounded-lg md:rounded-xl p-3 md:p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <motion.div
                animate={{
                  backgroundColor: animationPhase === 'complete' ? '#5F8F84' : 'hsl(var(--muted-foreground) / 0.3)',
                }}
                transition={{ duration: 0.4 }}
                className="w-2 h-2 rounded-full"
              />
              
              <AnimatePresence mode="wait">
                <motion.span
                  key={animationPhase === 'complete' ? 'synced' : 'waiting'}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.3 }}
                  className={`text-xs md:text-sm font-medium ${
                    animationPhase === 'complete' ? 'text-primary' : 'text-muted-foreground'
                  }`}
                >
                  {animationPhase === 'complete' ? 'All systems synced' : 'Connect your tools'}
                </motion.span>
              </AnimatePresence>
            </div>

            <AnimatePresence>
              {animationPhase === 'complete' && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.3 }}
                  className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-primary/10"
                >
                  <motion.div
                    animate={{ scale: [1, 1.15, 1] }}
                    transition={{ duration: 2.5, repeat: Infinity }}
                    className="w-1.5 h-1.5 rounded-full bg-primary"
                  />
                  <span className="text-[10px] md:text-xs font-medium text-primary">Live</span>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};

// Inbox Visual - Elevated, cinematic light-mode version
const InboxVisual = () => {
  return (
    <div className="relative w-full h-full flex items-center justify-center overflow-visible md:overflow-hidden">
      {/* Subtle ambient glow */}
      <div 
        className="absolute inset-0 opacity-40"
        style={{
          background: 'radial-gradient(ellipse 70% 50% at 50% 50%, hsl(var(--primary) / 0.08) 0%, transparent 60%)'
        }}
      />
      
      {/* Floating channel signals - distributed with gentle drift */}
      {[
        { icon: Phone, label: "Voice", x: -140, y: -70, delay: 0, color: "blue" },
        { icon: MessageSquare, label: "SMS", x: 130, y: -55, delay: 0.3, color: "green" },
        { icon: MessageSquare, label: "Chat", x: 145, y: 50, delay: 0.6, color: "purple" },
      ].map((channel) => (
        <motion.div
          key={channel.label}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ 
            opacity: [0, 0.95, 0.85],
            scale: [0.9, 1, 0.97],
            x: [channel.x * 1.1, channel.x, channel.x * 0.95],
            y: [channel.y * 1.1, channel.y, channel.y * 0.9],
          }}
          transition={{ 
            duration: 6,
            delay: channel.delay,
            repeat: Infinity,
            repeatType: "reverse",
            ease: "easeInOut"
          }}
          className="absolute z-10"
          style={{ left: '50%', top: '50%' }}
        >
          <div 
            className="px-3 md:px-4 py-2 md:py-2.5 rounded-xl bg-card border border-border shadow-lg"
            style={{ 
              boxShadow: channel.color === "blue" ? '0 8px 24px rgba(59, 130, 246, 0.12)' :
                         channel.color === "green" ? '0 8px 24px rgba(34, 197, 94, 0.12)' :
                         '0 8px 24px rgba(168, 85, 247, 0.12)'
            }}
          >
            <div className="flex items-center gap-2">
              <div className={`
                w-7 h-7 md:w-8 md:h-8 rounded-lg flex items-center justify-center
                ${channel.color === "blue" ? "bg-blue-100" :
                  channel.color === "green" ? "bg-green-100" :
                  "bg-purple-100"}
              `}>
                <channel.icon className={`w-3.5 h-3.5 md:w-4 md:h-4
                  ${channel.color === "blue" ? "text-blue-600" :
                    channel.color === "green" ? "text-green-600" :
                    "text-purple-600"}
                `} />
              </div>
              <span className="text-xs md:text-sm font-medium text-foreground">{channel.label}</span>
            </div>
          </div>
        </motion.div>
      ))}

      {/* Subtle converging particles */}
      {[...Array(6)].map((_, i) => (
        <motion.div
          key={`particle-${i}`}
          className="absolute w-1 h-1 rounded-full bg-primary/40"
          style={{ left: '50%', top: '50%' }}
          initial={{ 
            opacity: 0,
            x: (Math.cos(i * Math.PI / 3) * 160),
            y: (Math.sin(i * Math.PI / 3) * 120),
          }}
          animate={{ 
            opacity: [0, 0.5, 0],
            x: [Math.cos(i * Math.PI / 3) * 160, 0],
            y: [Math.sin(i * Math.PI / 3) * 120, 0],
          }}
          transition={{
            duration: 4,
            delay: i * 0.5,
            repeat: Infinity,
            ease: "easeIn"
          }}
        />
      ))}

      {/* Central unified inbox card */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="relative z-20"
      >
        {/* Soft glow behind card */}
        <div 
          className="absolute -inset-6 rounded-3xl opacity-50"
          style={{
            background: 'radial-gradient(ellipse at center, hsl(var(--primary) / 0.1) 0%, transparent 70%)'
          }}
        />
        
        {/* Main inbox card */}
        <div 
          className="relative bg-card rounded-2xl border border-border shadow-xl"
          style={{ 
            width: '260px',
            boxShadow: '0 0 40px rgba(95, 143, 132, 0.08), 0 20px 40px rgba(0,0,0,0.08)' 
          }}
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-border bg-muted/30 rounded-t-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Inbox className="w-3.5 h-3.5 text-primary" />
                </div>
                <div>
                  <div className="text-xs font-semibold text-foreground">Unified Inbox</div>
                  <div className="text-[9px] text-muted-foreground">All channels • Real-time</div>
                </div>
              </div>
              <motion.div
                animate={{ scale: [1, 1.3, 1], opacity: [0.7, 1, 0.7] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="w-2 h-2 rounded-full bg-primary"
              />
            </div>
          </div>

          {/* Message list */}
          <div className="p-2.5 space-y-1.5">
            {[
              { type: "voice", name: "Sarah M.", preview: "Voicemail received", time: "Just now", unread: true },
              { type: "sms", name: "John D.", preview: "Thanks for the reminder!", time: "2m ago", unread: true },
              { type: "email", name: "Acme Corp", preview: "Re: Partnership inquiry", time: "5m ago", unread: false },
              { type: "chat", name: "Support Bot", preview: "New lead captured", time: "8m ago", unread: false },
            ].map((message, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 + i * 0.08 }}
                className={`
                  flex items-start gap-2.5 p-2.5 rounded-xl transition-colors
                  ${message.unread ? 'bg-primary/5 border border-primary/10' : 'bg-muted/20 border border-transparent'}
                `}
              >
                <div className={`
                  w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0
                  ${message.type === "voice" ? "bg-blue-100" :
                    message.type === "sms" ? "bg-green-100" :
                    message.type === "email" ? "bg-amber-100" :
                    "bg-purple-100"}
                `}>
                  {message.type === "voice" && <Phone className="w-3 h-3 text-blue-600" />}
                  {message.type === "sms" && <MessageSquare className="w-3 h-3 text-green-600" />}
                  {message.type === "email" && <Mail className="w-3 h-3 text-amber-600" />}
                  {message.type === "chat" && <MessageSquare className="w-3 h-3 text-purple-600" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-1.5 mb-0.5">
                    <span className={`text-[10px] font-medium truncate ${message.unread ? 'text-foreground' : 'text-muted-foreground'}`}>
                      {message.name}
                    </span>
                    <span className="text-[9px] text-muted-foreground flex-shrink-0">{message.time}</span>
                  </div>
                  <p className={`text-[9px] truncate ${message.unread ? 'text-foreground/70' : 'text-muted-foreground'}`}>
                    {message.preview}
                  </p>
                </div>
                {message.unread && (
                  <motion.div 
                    animate={{ opacity: [0.6, 1, 0.6] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0 mt-1" 
                  />
                )}
              </motion.div>
            ))}
          </div>

          {/* Footer status */}
          <div className="px-3 py-2 border-t border-border bg-muted/20 rounded-b-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <div className="flex -space-x-1">
                  {["blue", "green", "amber", "purple", "pink"].map((color, i) => (
                    <div 
                      key={i}
                      className={`w-3 h-3 rounded-full border-2 border-card
                        ${color === "blue" ? "bg-blue-400" :
                          color === "green" ? "bg-green-400" :
                          color === "amber" ? "bg-amber-400" :
                          color === "purple" ? "bg-purple-400" :
                          "bg-pink-400"}
                      `}
                    />
                  ))}
                </div>
                <span className="text-[9px] text-muted-foreground">5 channels synced</span>
              </div>
              <motion.div
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="text-[9px] text-primary font-medium"
              >
                Live
              </motion.div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Workflows Visual
const WorkflowsVisual = () => {
  return (
    <div className="relative h-full w-full bg-gradient-to-br from-muted/20 via-background to-muted/10 rounded-xl flex items-center justify-center">
      <div 
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: 'radial-gradient(circle, hsl(var(--foreground)) 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}
      />
      
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="relative flex flex-col items-center scale-[0.75] md:scale-[0.85] origin-center"
      >
        {/* Node 1: Lead Entry */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.4 }}
          className="relative z-10 flex items-center gap-2.5 px-4 py-2.5 rounded-xl bg-card border border-border shadow-lg"
        >
          <div className="w-7 h-7 rounded-lg bg-violet-100 flex items-center justify-center">
            <svg className="w-4 h-4 text-violet-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
          </div>
          <span className="text-sm font-medium text-foreground">Lead Entry</span>
        </motion.div>

        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.3 }}
          className="w-2 h-2 rounded-full bg-border my-1 z-10"
        />

        {/* Node 2: AI Call */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.4 }}
          className="relative z-10 flex items-center gap-2.5 px-4 py-2.5 rounded-xl bg-card border border-border shadow-lg mt-2"
        >
          <div className="w-7 h-7 rounded-lg bg-purple-100 flex items-center justify-center">
            <svg className="w-4 h-4 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
          </div>
          <span className="text-sm font-medium text-foreground">AI Call</span>
          <div className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-purple-100 border border-purple-200 flex items-center justify-center">
            <svg className="w-3 h-3 text-purple-600" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
            </svg>
          </div>
        </motion.div>

        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.5 }}
          className="w-2 h-2 rounded-full bg-border my-1 z-10"
        />

        {/* Node 3: Active AI Conversation */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5, duration: 0.5 }}
          className="relative z-10 px-5 py-4 rounded-2xl bg-card border border-primary/30 shadow-xl mt-2"
          style={{ boxShadow: '0 0 40px rgba(95, 143, 132, 0.15), 0 8px 32px rgba(0,0,0,0.1)' }}
        >
          <div className="flex items-center justify-between gap-6 mb-3">
            <span className="text-sm font-semibold text-foreground">Active AI Conversation</span>
            <motion.div 
              animate={{ opacity: [1, 0.5, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-primary/15 border border-primary/25"
            >
              <div className="w-1.5 h-1.5 rounded-full bg-primary" />
              <span className="text-[10px] font-medium text-primary uppercase tracking-wide">Live</span>
            </motion.div>
          </div>
          <div className="flex items-center justify-center gap-0.5 h-8">
            {Array.from({ length: 32 }).map((_, i) => (
              <motion.div
                key={i}
                className="w-1 rounded-full bg-gradient-to-t from-primary to-primary/60"
                animate={{
                  height: [8, 4 + Math.sin(i * 0.5) * 16 + Math.random() * 8, 8],
                }}
                transition={{
                  duration: 0.8 + Math.random() * 0.4,
                  repeat: Infinity,
                  repeatType: "reverse",
                  delay: i * 0.03,
                }}
              />
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.7 }}
          className="w-2 h-2 rounded-full bg-border my-1 z-10"
        />

        {/* Branch: Contacted = False / True */}
        <div className="flex items-start gap-8 mt-2 relative z-10">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 0.5, x: 0 }}
            transition={{ delay: 0.9, duration: 0.4 }}
            className="flex items-center gap-2 px-3 py-2 rounded-xl bg-muted/50 border border-border/50"
          >
            <div className="w-5 h-5 rounded-md bg-muted flex items-center justify-center">
              <svg className="w-3 h-3 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
            </div>
            <span className="text-xs text-muted-foreground">Contacted = False</span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.9, duration: 0.4 }}
            className="flex flex-col items-center"
          >
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-amber-50 border border-amber-200 shadow-lg"
              style={{ boxShadow: '0 0 20px rgba(245, 158, 11, 0.15)' }}
            >
              <div className="w-5 h-5 rounded-md bg-amber-100 flex items-center justify-center">
                <svg className="w-3 h-3 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <span className="text-xs font-medium text-amber-700">Contacted = True</span>
            </div>

            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 1.1 }}
              className="w-2 h-2 rounded-full bg-border my-1"
            />

            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.4 }}
              className="flex items-center gap-2 px-3 py-2 rounded-xl bg-card border border-border shadow-lg mt-1"
            >
              <div className="w-5 h-5 rounded-md bg-blue-100 flex items-center justify-center">
                <svg className="w-3 h-3 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <span className="text-xs font-medium text-foreground">Book Appointment</span>
            </motion.div>

            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 1.3 }}
              className="w-2 h-2 rounded-full bg-border my-1"
            />

            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.3, duration: 0.4 }}
              className="flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-50 border border-emerald-200 shadow-lg mt-1"
              style={{ boxShadow: '0 0 20px rgba(16, 185, 129, 0.15)' }}
            >
              <div className="w-5 h-5 rounded-md bg-emerald-100 flex items-center justify-center">
                <svg className="w-3 h-3 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="text-xs font-medium text-emerald-700">Send Confirmation</span>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

const visuals: Record<string, React.FC> = {
  voice: WhiteLabelVisual,
  chat: IntegrationsVisual,
  inbox: InboxVisual,
  workflows: WorkflowsVisual,
};

interface ProblemSectionProps {
  preloaderComplete?: boolean;
}

const ProblemSection = ({ preloaderComplete = true }: ProblemSectionProps) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const shouldAnimate = isInView && preloaderComplete;
  const [activeTab, setActiveTab] = useState("voice");
  const [isPaused, setIsPaused] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Auto-rotation
  useEffect(() => {
    if (isPaused) return;
    
    const interval = setInterval(() => {
      setActiveTab((current) => {
        const currentIndex = tabs.findIndex((t) => t.id === current);
        const nextIndex = (currentIndex + 1) % tabs.length;
        return tabs[nextIndex].id;
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [isPaused]);

  const ActiveIcon = tabs.find((t) => t.id === activeTab)?.icon || Phone;
  const content = tabContent[activeTab as keyof typeof tabContent];
  const VisualComponent = visuals[activeTab];

  return (
    <section id="problem" ref={ref} className="relative pt-16 pb-20 md:pt-24 md:pb-32 px-2 md:px-6 overflow-hidden">
      {/* Moderate green gradient background */}
      <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at center, rgba(16, 185, 129, 0.10) 0%, transparent 60%)' }} />
      
      <div className="w-full md:container md:mx-auto md:max-w-6xl relative z-10">
        {/* Section Label */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="flex justify-center mb-3 md:mb-4"
        >
          <span className="section-label text-muted-foreground">
            The Problem
          </span>
        </motion.div>

        {/* Headline */}
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl text-center text-foreground max-w-3xl mx-auto headline-display px-2"
        >
          Clients don't buy chaos.
          <br />
          They buy <span className="ayra-accent">platforms</span>.
        </motion.h2>

        {/* Subheadline */}
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-4 md:mt-6 text-base md:text-lg text-muted-foreground text-center max-w-2xl mx-auto body-text px-2"
        >
          You're not imagining it. Building a profitable AI agency with today's tools is nearly impossible.
        </motion.p>

        {/* Tabbed Feature Component */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-10 md:mt-16"
        >
          <div
            ref={containerRef}
            className="bg-card rounded-xl md:rounded-2xl shadow-xl border border-border p-3 md:p-6 lg:p-10 xl:p-12 min-h-[650px] md:min-h-0 pb-24 md:pb-0"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            {/* Tabs - Scrollable on mobile */}
            <div className="flex overflow-x-auto md:overflow-visible md:flex-wrap justify-start md:justify-between gap-2 md:gap-4 mb-6 md:mb-10 border-b border-border pb-4 md:pb-6 -mx-2 px-2 md:mx-0 md:px-0 scrollbar-hide">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 md:gap-3 px-3 md:px-4 py-2 md:py-3 rounded-lg md:rounded-xl transition-all duration-300 flex-shrink-0 ${
                    activeTab === tab.id
                      ? "bg-primary/10 border border-primary/20"
                      : "hover:bg-muted"
                  }`}
                >
                  <tab.icon
                    className={`w-4 md:w-5 h-4 md:h-5 transition-colors ${
                      activeTab === tab.id ? "text-primary" : "text-muted-foreground"
                    }`}
                  />
                  <div className="text-left">
                    <p
                      className={`text-sm md:text-base font-semibold transition-colors whitespace-nowrap ${
                        activeTab === tab.id ? "text-foreground" : "text-muted-foreground"
                      }`}
                    >
                      {tab.title}
                    </p>
                    <p className="text-[10px] md:text-xs text-muted-foreground hidden sm:block whitespace-nowrap">
                      {tab.shortDesc}
                    </p>
                  </div>
                </button>
              ))}
            </div>

            {/* Content */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
                className="flex flex-col md:grid md:grid-cols-5 gap-6 md:gap-8 items-center"
              >
                {/* Left side - Text */}
                <div className="w-full md:col-span-2 space-y-3 md:space-y-4">
                  <div className="w-10 md:w-12 h-10 md:h-12 rounded-lg md:rounded-xl bg-primary/10 flex items-center justify-center">
                    <ActiveIcon className="w-5 md:w-6 h-5 md:h-6 text-primary" />
                  </div>
                  <h3 className="text-xl md:text-2xl lg:text-3xl text-foreground headline whitespace-pre-line">
                    {content.title}
                  </h3>
                  <p className="text-sm md:text-base text-muted-foreground body-text whitespace-pre-line">
                    {content.description}
                  </p>
                  <Button variant="outline" className="group text-sm">
                    {content.buttonText}
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>

                {/* Right side - Visual */}
                <div className="w-full md:col-span-3 h-[300px] sm:h-[340px] md:h-[380px] min-h-[300px] sm:min-h-[340px] md:min-h-[380px] overflow-visible md:overflow-hidden">
                  <VisualComponent />
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProblemSection;